import { useEffect, useState } from 'react';

import { Database01Icon } from '@hugeicons/core-free-icons';
import { CandidateCheckScreens } from 'constants/candidate_screens';
import { buildNavPath as buildNavPathHelper } from 'helpers/candidate_application/form-page-helpers';
import { now } from 'helpers/date';
import { useQueryParams } from 'hooks/query-params';
import { useCandidateApplicationUploadActions } from 'hooks/upload-actions';
import { useUserAgent } from 'hooks/user-agent';
import CandidatePage from 'layouts/CandidatePage/CandidatePage';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { shouldSubmitCandidate } from 'utils/checks-utils';

import { Form } from 'components/Form/Form/Form';

import {
  saveCheckFormAnswers,
  selectCandidateApplication,
  submitCandidateApplication,
  updateCandidateCheck,
} from 'api/candidate-application-slice';

import { IconComponent } from './../../../components/Icon/IconComponent';
import styles from './CandidateQuestionnaire.module.scss';

const CandidateQuestionnaire = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user_agent } = useUserAgent();
  const { token } = useParams();

  const [subCheckId] = useQueryParams(['subCheckId']);
  const [admin] = useQueryParams(['admin']);

  // Helper to build navigation path with admin parameter preserved
  const buildNavPath = path => buildNavPathHelper(path, admin);

  const {
    application: { candidate, candidate_checks, brand, check_types, forms },
    fetched,
  } = useSelector(selectCandidateApplication);

  const { getUpload, signUpload, createUpload, deleteUpload } =
    useCandidateApplicationUploadActions(token);

  const [loading, setLoading] = useState(false);
  const [questionnaireCheck, setQuestionnaireCheck] = useState();
  const [form, setForm] = useState();

  // Set questionnaire form if checks
  useEffect(() => {
    if (!fetched) return;
    const check = candidate_checks.find(check => check.type === 'candidate_questionnaire');
    setQuestionnaireCheck(check);
    if (subCheckId) {
      const formId = check.sub_checks.find(subCheck => subCheck.id == subCheckId).details.id;
      setForm(forms.find(form => form.id === formId));
    } else {
      const formId = check.details.form_id;
      setForm(forms.find(form => form.id === formId));
    }
  }, [fetched, subCheckId]);

  const onSubmit = async data => {
    setLoading(true);
    const { payload: updatedCheck } = await dispatch(
      saveCheckFormAnswers({
        token,
        id: questionnaireCheck.id,
        params: { answers: data, user_agent, sub_check_id: subCheckId },
      }),
    );

    const unsubmittedQuestionnaires = updatedCheck.sub_checks?.filter(
      subCheck => !subCheck.completed_at,
    );

    if (unsubmittedQuestionnaires?.length < 1) {
      await dispatch(
        updateCandidateCheck({ token, id: updatedCheck.id, data: { submitted_at: now() } }),
      );
    }

    if (
      unsubmittedQuestionnaires?.length < 1 &&
      shouldSubmitCandidate(updatedCheck, candidate_checks, check_types)
    ) {
      await dispatch(submitCandidateApplication({ token, user_agent }));
      setLoading(false);
      navigate(
        buildNavPath(`/form_submission/candidate/${CandidateCheckScreens.SUCCESS}/${token}`),
      );
    } else {
      setLoading(false);
      navigate(buildNavPath(`/form_submission/candidate/dashboard/${token}`));
    }
  };

  return (
    <CandidatePage loading={loading} token={token} withTopLogo brand={brand}>
      <CandidatePage.View>
        <CandidatePage.Card className="d-flex flex-column u-gap-large">
          <div className="d-flex flex-column u-gap">
            <IconComponent icon={Database01Icon} className={styles.icon} size="xxlarge" />
            <h1 className="primary-text u-font-weight-500 u-margin-0">{form?.name}</h1>
          </div>
          <Form
            candidateId={candidate.id}
            form={form}
            brand={brand}
            textParams={{
              candidate: candidate.first_name,
              candidateFirstName: candidate.first_name,
              candidateFullName: candidate.name,
              candidateLastName: candidate.last_name,
              job: candidate.job_role,
            }}
            onSubmit={onSubmit}
            getCandidateUpload={getUpload}
            deleteCandidateUpload={deleteUpload}
            signCandidateUpload={signUpload}
            createCandidateUpload={createUpload}
          />
        </CandidatePage.Card>
      </CandidatePage.View>
    </CandidatePage>
  );
};

export default CandidateQuestionnaire;
